import type {FC} from 'react'
import {useState} from 'react'
import {IconButton, Menu, MenuItem, Tooltip} from '@mui/material'
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined'

import {useSetDocumentVisibilityMutation} from '@apis/aiCoreAPI'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import {useSnackbar} from '@hooks/useSnackbar'
import type {DocumentVisibility} from '@interfaces/alpinist/document'
import {ICON_BY_SCOPE} from '@containers/Dashboards/Settings/components/alpinist/kb/DocumentScopeChip'

interface Props {
  documentId: string
  documentName: string
  visibility: DocumentVisibility
}

/**
 * Where a file dropped into a chat is kept (AB#5992).
 *
 * An attachment is searchable in its conversation and nowhere else. That is the
 * right default — nobody wants every screenshot in the organization's Knowledge
 * Base — but without a way out it is a dead end: the file the user just
 * discussed cannot be found from anywhere else, and the only remedy is to
 * upload it a second time from Settings.
 *
 * Every move is offered, in both directions, including back out again. Saving
 * used to be one-way: the control disappeared the moment it worked, so a file
 * kept by mistake could only be deleted. A menu rather than one button because
 * these are genuinely different decisions, and handing a file to the whole
 * organization is not something to do by accident on a stray click.
 */
const AttachmentKeepButton: FC<Props> = ({documentId, documentName, visibility}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [t] = useCustomTranslation('alpinist')
  const [showNotification] = useSnackbar()
  const [setVisibility, {isLoading}] = useSetDocumentVisibilityMutation()

  const saved = visibility !== 'conversation'

  const move = async (target: DocumentVisibility): Promise<void> => {
    setAnchor(null)
    try {
      await setVisibility({id: documentId, visibility: target}).unwrap()
      showNotification({
        message: t(`attachment_kept_${target}`, {name: documentName}),
        severity: 'success',
      })
    } catch (_error) {
      showNotification({
        message: t('attachment_keep_failed', {name: documentName}),
        severity: 'error',
      })
    }
  }

  // The destinations, minus wherever the file already is — an item that would
  // be a no-op reads as an option and answers with a 409.
  const destinations = (['personal', 'tenant', 'conversation'] as DocumentVisibility[]).filter(
    (d) => d !== visibility,
  )

  // Saved, the button wears the scope's own icon, the one the Knowledge Base
  // list uses for that row — the previous single icon said the file was saved
  // but not which Knowledge Base it went to. Unsaved it is the Knowledge Base
  // icon, because that is the action on offer rather than a state.
  const ScopeIcon = ICON_BY_SCOPE[visibility] ?? DocumentScannerOutlinedIcon

  const label = saved ? t('attachment_saved') : t('attachment_keep')

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          size="small"
          aria-label={label}
          data-testid="attachment-keep-button"
          disabled={isLoading}
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          {saved ? (
            <ScopeIcon fontSize="small" color="primary" />
          ) : (
            <DocumentScannerOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {destinations.map((destination) => {
          const Icon = ICON_BY_SCOPE[destination]
          return (
            <MenuItem
              key={destination}
              data-testid={`attachment-keep-${destination}`}
              onClick={() => void move(destination)}
            >
              <Icon fontSize="small" sx={{mr: 1}} />
              {t(`attachment_keep_${destination}`)}
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

export default AttachmentKeepButton
